/** Работа с датами проживания.
 *
 * Все даты — строки `YYYY-MM-DD` без времени и часового пояса. Парсить их через
 * `new Date(iso)` нельзя: строка трактуется как UTC и в отрицательных
 * часовых поясах сдвигается на день назад.
 */

export type IsoDate = string

export function parse(iso: IsoDate): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function toIso(date: Date): IsoDate {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const date = parse(iso)
  date.setDate(date.getDate() + days)
  return toIso(date)
}

export function today(): IsoDate {
  return toIso(new Date())
}

/** Число ночей между датами: выезд не считается. */
export function nightsBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000)
}

/** Список дат от `from` включительно до `to` включительно. */
export function rangeInclusive(from: IsoDate, to: IsoDate): IsoDate[] {
  const result: IsoDate[] = []
  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) {
    result.push(cursor)
  }
  return result
}

export function isWeekend(iso: IsoDate): boolean {
  const day = parse(iso).getDay()
  return day === 0 || day === 6
}

export function isToday(iso: IsoDate): boolean {
  return iso === today()
}

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]
const MONTHS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'мая', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
]

export function weekdayShort(iso: IsoDate): string {
  return WEEKDAYS[parse(iso).getDay()]
}

export function dayNumber(iso: IsoDate): string {
  return String(parse(iso).getDate())
}

export function monthShort(iso: IsoDate): string {
  return MONTHS_SHORT[parse(iso).getMonth()]
}

export function formatLong(iso: IsoDate): string {
  const date = parse(iso)
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`
}

export function formatRange(from: IsoDate, to: IsoDate): string {
  return `${formatLong(from)} — ${formatLong(to)}`
}
